/**
 * Media Layer Constants
 */

import type { PlayerStyles } from '../types'

/** Shared ceiling for non-video composer attachments (images, documents, audio). */
export const ATTACHMENT_MAX_FILE_SIZE = 50 * 1024 * 1024

/** Composer video upload ceiling (rich post/comment editors, media gallery). */
export const ATTACHMENT_VIDEO_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024

/** Voice clip / uploaded audio max duration (30 minutes). */
export const VOICE_NOTE_MAX_DURATION_SEC = 30 * 60

/** MIME types for composer document/file pickers (`accept`). */
export const ATTACHMENT_ACCEPTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed',
] as const

export type AttachmentAcceptedDocumentType = (typeof ATTACHMENT_ACCEPTED_DOCUMENT_TYPES)[number]

/** MIME types for voice-note file pickers (`accept`). */
export const VOICE_NOTE_ACCEPTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/ogg',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
  'audio/x-m4a',
  'audio/m4a',
] as const

export type VoiceNoteAcceptedAudioType = (typeof VOICE_NOTE_ACCEPTED_AUDIO_TYPES)[number]

/** Documents + dropped audio for surfaces that share one file picker (e.g. post editor). */
export const ATTACHMENT_ACCEPTED_FILE_TYPES = [...ATTACHMENT_ACCEPTED_DOCUMENT_TYPES, ...VOICE_NOTE_ACCEPTED_AUDIO_TYPES] as const

/** MIME types for image pickers (`accept`) — raster/web only. */
export const ATTACHMENT_ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const

export type AttachmentAcceptedImageType = (typeof ATTACHMENT_ACCEPTED_IMAGE_TYPES)[number]

/** MIME types for video pickers (`accept`) — common web formats. */
export const ATTACHMENT_ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const

export type AttachmentAcceptedVideoType = (typeof ATTACHMENT_ACCEPTED_VIDEO_TYPES)[number]

/** SVG — rich composer surfaces only (chat/post/comment editors, MediaPicker). */
export const ATTACHMENT_ACCEPTED_IMAGE_SVG_TYPE = 'image/svg+xml' as const

/** Legacy Windows video containers — composer surfaces only. */
export const ATTACHMENT_ACCEPTED_VIDEO_LEGACY_TYPES = ['video/x-msvideo', 'video/x-ms-wmv'] as const

/** Composer image accept list (base raster types + SVG). */
export const COMPOSER_ACCEPTED_IMAGE_TYPES = [...ATTACHMENT_ACCEPTED_IMAGE_TYPES, ATTACHMENT_ACCEPTED_IMAGE_SVG_TYPE] as const

/** Composer video accept list (base web formats + AVI/WMV). */
export const COMPOSER_ACCEPTED_VIDEO_TYPES = [...ATTACHMENT_ACCEPTED_VIDEO_TYPES, ...ATTACHMENT_ACCEPTED_VIDEO_LEGACY_TYPES] as const

/** Placeholder image URL for audio files without a cover image */
export const AUDIO_FILE_PLACEHOLDER = 'https://storage.googleapis.com/revex-membership-production/assets/audio-placeholder.svg'

/** Default thumbnail for audio lessons in fullSize mode */
export const AUDIO_LESSON_THUMBNAIL = 'https://storage.googleapis.com/revex-membership-staging/assets/audio-lesson-thumbnail.webp'

/** Base URL for Firebase Storage (used to detect and skip Firebase Storage URLs in CDN replacement) */
export const FIREBASE_STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com'

/** Default controls and surface colors for DRM video playback */
export const DEFAULT_DRM_PLAYER_STYLES: PlayerStyles = {
  controlsBgColor: 'linear-gradient(to top, #00000040, #00000000)',
  primaryColor: '#d7d7d799',
  borderRadius: '0.375rem',
  menuBgColor: '#ffffff',
  textColor: '#lightgray',
  volumeBgColor: 'transparent',
  videoBgColor: '#000000',
}
